import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { formatDayTime } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { LoginCta } from '../../ui/LoginCta'
import { useSession } from '../../app/session'
import { useLanguage } from '../../app/language'
import { loginPath } from '../../app/loginReturn'
import { env } from '../../config/env'
import { useInbox, useMarkRead, useUnreadCount, type Notification } from './api'

/**
 * Центр уведомлений: новости о заказах и баллах, новые сверху. Непрочитанное
 * выделено точкой и жирным заголовком. Нажатие отмечает новость прочитанной и,
 * если она о заказе, открывает заказ; «Прочитать все» — разом.
 */
export function NotificationsScreen() {
  const { t } = useTranslation(['account', 'common'])
  const navigate = useNavigate()
  const { isAuthenticated } = useSession()
  const { language } = useLanguage()
  const inbox = useInbox(isAuthenticated, language)
  const unread = useUnreadCount(isAuthenticated)
  const markRead = useMarkRead()
  const { search } = useLocation()
  const items = inbox.data?.pages.flatMap((page) => page.items) ?? []

  // Нажали на push в нашем приложении, а сессии нет: оболочка открыла эту
  // страницу заново (`?isFlutterApp=1`) — сразу на вход и обратно сюда.
  if (env.features.push && !isAuthenticated && new URLSearchParams(search).has('isFlutterApp')) {
    return <Navigate to={loginPath('/notifications')} replace />
  }

  const open = (item: Notification) => {
    if (item.readAt === 0) markRead.mutate([item.id])
    if (item.orderId) void navigate(`/orders/${item.orderId}`)
  }

  return (
    <section className="screen cart-wrap">
      <div className="mhead">
        <BackButton to="/" />
        <h1>{t('account:inbox.title')}</h1>
        {unread.data ? (
          <Press
            squish="chip"
            className="mhead-act"
            onPress={() => {
              markRead.mutate([])
            }}
          >
            {t('account:inbox.readAll')}
          </Press>
        ) : null}
      </div>

      {isAuthenticated ? (
        <div className="card">
          {inbox.isPending ? <Loading what={t('common:state.loading')} shape="lines" /> : null}
          {inbox.isError ? (
            <Failed
              error={inbox.error}
              onRetry={() => {
                void inbox.refetch()
              }}
            />
          ) : null}
          {inbox.data && items.length === 0 ? (
            <div className="list-empty">{t('account:inbox.empty')}</div>
          ) : null}
          {items.length > 0 ? (
            <ul className="ntf-list">
              {items.map((item) => (
                <li key={item.id}>
                  <Press
                    squish="row"
                    className="ntf"
                    data-unread={item.readAt === 0 || undefined}
                    onPress={() => {
                      open(item)
                    }}
                  >
                    <span className="ntf-ic" aria-hidden="true">
                      <Icon name={item.orderId ? 'i-box' : 'i-gift'} size={20} />
                    </span>
                    <span className="ntf-txt">
                      <span className="ntf-t">
                        {item.readAt === 0 ? (
                          <span className="ntf-dot">
                            <span className="visually-hidden">{t('account:inbox.unread')}</span>
                          </span>
                        ) : null}
                        {item.title}
                      </span>
                      {item.body ? <span className="ntf-b">{item.body}</span> : null}
                      <span className="ntf-at num">{formatDayTime(item.at)}</span>
                    </span>
                    {item.orderId ? <Icon name="i-chev" size={16} /> : null}
                  </Press>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <LoginCta
          onSignIn={() => {
            void navigate(loginPath('/notifications'))
          }}
        />
      )}

      {inbox.hasNextPage ? (
        <Button
          variant="ghost"
          className="ntf-more"
          disabled={inbox.isFetchingNextPage}
          onPress={() => {
            void inbox.fetchNextPage()
          }}
        >
          {t('common:actions.showMore')}
        </Button>
      ) : null}
    </section>
  )
}
